import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { renderSite } from '@sarnia/shared';
import { editorStore, useEditorSelector } from './editorStore';

// Published pages hide an image the owner has not chosen yet; while editing it stays visible (and keeps its column).
const EDITOR_PREVIEW_CSS =
  '.image-placeholder{display:flex!important}@media (min-width:901px){.block--image-text .block-inner:has(.image-placeholder),.hero-inner:has(.image-placeholder){grid-template-columns:1fr 1fr!important}}';

function makePreviewDocument(html: string, css: string): string {
  const trimmed = html.trim();

  if (/^<!doctype/i.test(trimmed) || /^<html/i.test(trimmed)) {
    // The renderer returns a full page that links ./site.css and ./site.js, which do not exist inside the
    // preview frame: inline the stylesheet and drop the external script so the preview looks like the real site.
    const styleTag = `<style>${css}${EDITOR_PREVIEW_CSS}</style>`;
    // Published pages forbid inline styles through a CSP meta tag; the preview inlines the CSS and is already sandboxed (no scripts).
    const withoutCsp = trimmed.replace(/<meta[^>]*http-equiv=["']Content-Security-Policy["'][^>]*>/i, () => '');
    const withoutScript = withoutCsp.replace(/<script[^>]*src=["']\.\/site\.js["'][^>]*><\/script>/i, () => '');
    if (/<link[^>]*href=["']\.\/site\.css["'][^>]*>/i.test(withoutScript)) {
      return withoutScript.replace(/<link[^>]*href=["']\.\/site\.css["'][^>]*>/i, () => styleTag);
    }
    return withoutScript.replace(/<\/head>/i, () => `${styleTag}</head>`);
  }

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <style>${css}${EDITOR_PREVIEW_CSS}</style>
  </head>
  <body>${html}</body>
</html>`;
}

export default function Canvas() {
  const site = useEditorSelector((state) => state.site);
  const assets = useEditorSelector((state) => state.assets);
  const selectedBlockId = useEditorSelector((state) => state.selectedBlockId);
  const previewDevice = useEditorSelector((state) => state.previewDevice);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const loadedDocumentRef = useRef<Document | null>(null);
  const clickHandlerRef = useRef<((event: MouseEvent) => void) | null>(null);
  const selectedBlockIdRef = useRef(selectedBlockId);
  const selectBlockRef = useRef<(blockId: string | null) => void>(() => {});
  const updateBlockPropsRef = useRef<(blockId: string, patch: Record<string, unknown>) => void>(() => {});

  const [loadedDocument, setLoadedDocument] = useState<Document | null>(null);

  useEffect(() => {
    selectedBlockIdRef.current = selectedBlockId;
  }, [selectedBlockId]);

  useEffect(() => {
    selectBlockRef.current = (blockId: string | null) => {
      editorStore.selectBlock(blockId);
    };
    updateBlockPropsRef.current = (blockId: string, patch: Record<string, unknown>) => {
      editorStore.updateBlockProps(blockId, patch);
    };
  }, []);

  const removePreviousDocumentListener = useCallback(() => {
    const doc = loadedDocumentRef.current;
    const handler = clickHandlerRef.current;
    if (doc && doc.body && handler) {
      doc.body.removeEventListener('click', handler);
    }
    loadedDocumentRef.current = null;
    clickHandlerRef.current = null;
  }, []);

  const openInlineEditor = useCallback((element: HTMLElement, blockId: string, field: string): void => {
    const doc = element.ownerDocument;
    if (!doc) {
      return;
    }

    const existing = doc.querySelector('.sarnia-inline-editor');
    existing?.remove();

    const rect = element.getBoundingClientRect();
    const originalText = element.textContent ?? '';
    const view = doc.defaultView;
    const cs = view ? view.getComputedStyle(element) : null;
    // Nearest solid background behind the text, so the editor looks like the page and the text stays readable.
    let solid = '#ffffff';
    if (view) {
      for (let node: HTMLElement | null = element; node; node = node.parentElement) {
        const bg = view.getComputedStyle(node).backgroundColor;
        if (bg && bg !== 'transparent' && !/rgba\(\s*\d+,\s*\d+,\s*\d+,\s*0\s*\)/.test(bg)) {
          solid = bg;
          break;
        }
      }
    }
    // Short labels and headings save on Enter; long copy keeps Enter for new lines and saves on Ctrl/Cmd+Enter.
    const multiline = originalText.length > 90 || element.tagName === 'P';
    const textarea = doc.createElement('textarea');
    textarea.className = 'sarnia-inline-editor';
    textarea.value = originalText;
    textarea.rows = 1;
    textarea.setAttribute('aria-label', 'Edit text');
    textarea.spellcheck = true;
    const st = textarea.style;
    st.position = 'fixed';
    st.left = `${Math.max(rect.left - 6, 0)}px`;
    st.top = `${Math.max(rect.top - 4, 0)}px`;
    st.width = `${Math.max(rect.width + 12, 160)}px`;
    st.boxSizing = 'border-box';
    st.padding = '4px 6px';
    st.margin = '0';
    st.border = '2px solid #4f46e5';
    st.borderRadius = '6px';
    st.outline = 'none';
    st.boxShadow = '0 6px 24px rgba(0,0,0,.25)';
    st.resize = 'none';
    st.overflow = 'hidden';
    st.background = solid;
    if (cs) {
      st.fontFamily = cs.fontFamily;
      st.fontSize = cs.fontSize;
      st.fontWeight = cs.fontWeight;
      st.fontStyle = cs.fontStyle;
      st.lineHeight = cs.lineHeight;
      st.letterSpacing = cs.letterSpacing;
      st.textTransform = cs.textTransform;
      st.textAlign = cs.textAlign;
      st.color = cs.color;
    }
    st.zIndex = '999';
    doc.body.appendChild(textarea);
    const grow = (): void => {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(textarea.scrollHeight + 4, rect.height)}px`;
    };
    grow();
    textarea.addEventListener('input', grow);
    textarea.focus();
    textarea.select();

    let finished = false;

    const finish = (commit: boolean): void => {
      if (finished) {
        return;
      }
      finished = true;

      if (commit) {
        const value = textarea.value;
        if (value !== originalText) {
          updateBlockPropsRef.current(blockId, { [field]: value });
        }
      }

      textarea.remove();
    };

    textarea.addEventListener('blur', () => finish(true), { once: true });
    textarea.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        finish(false);
      } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey || (!multiline && !event.shiftKey))) {
        event.preventDefault();
        finish(true);
      }
    });
  }, []);

  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    const doc = iframe.contentDocument;
    if (!doc || !doc.body) {
      return;
    }

    removePreviousDocumentListener();

    const handleClick = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null;
      if (!target || typeof target.closest !== 'function') {
        return;
      }

      const interactive = target.closest('a, button') as HTMLElement | null;
      if (interactive) {
        event.preventDefault();
      }

      const blockElement = target.closest('[data-editor-block]') as HTMLElement | null;
      if (!blockElement) {
        if (selectedBlockIdRef.current !== null) {
          selectBlockRef.current(null);
        }
        return;
      }

      const blockId = blockElement.getAttribute('data-editor-block');
      if (!blockId) {
        if (selectedBlockIdRef.current !== null) {
          selectBlockRef.current(null);
        }
        return;
      }

      if (selectedBlockIdRef.current !== blockId) {
        selectBlockRef.current(blockId);
      }

      const fieldElement = target.closest('[data-field]') as HTMLElement | null;
      if (fieldElement && blockElement.contains(fieldElement)) {
        const field = fieldElement.getAttribute('data-field');
        if (field) {
          openInlineEditor(fieldElement, blockId, field);
        }
      }
    };

    doc.body.addEventListener('click', handleClick, { passive: false });
    clickHandlerRef.current = handleClick;
    loadedDocumentRef.current = doc;
    setLoadedDocument(doc);
  }, [removePreviousDocumentListener, openInlineEditor]);

  useEffect(() => {
    return () => {
      removePreviousDocumentListener();
    };
  }, [removePreviousDocumentListener]);

  useEffect(() => {
    if (!loadedDocument) {
      return;
    }

    const doc = loadedDocument;
    if (!doc.head) {
      return;
    }

    let styleEl = doc.getElementById('sarnia-selection') as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = doc.createElement('style');
      styleEl.id = 'sarnia-selection';
      doc.head.appendChild(styleEl);
    }

    if (!selectedBlockId) {
      styleEl.textContent = '';
    } else {
      const escapedId = CSS.escape(selectedBlockId);
      styleEl.textContent = `[data-editor-block="${escapedId}"] { outline: 2px solid #4f46e5; outline-offset: -2px; }`;
    }
  }, [loadedDocument, selectedBlockId]);

  const previewDocument = useMemo(() => {
    if (!site) {
      return null;
    }
    const rendered = renderSite(site, assets, { editorMarkers: true });
    return makePreviewDocument(rendered.html, rendered.css);
  }, [site, assets]);

  if (!site) {
    return <div className="canvas-loading">Loading canvas…</div>;
  }

  return (
    <div className={`canvas-shell canvas-shell--${previewDevice}`}>
      {previewDocument ? (
        <iframe
          ref={iframeRef}
          className="canvas-iframe"
          title="Site preview"
          sandbox="allow-same-origin"
          srcDoc={previewDocument}
          onLoad={handleIframeLoad}
        />
      ) : (
        <div className="canvas-loading">Preparing preview…</div>
      )}
      <span className="canvas-selected">
        Selected block: {selectedBlockId ?? 'none'}
      </span>
    </div>
  );
}
